import { useState } from 'react';
import { Navigate, useNavigate } from 'react-router';
import { useAuth } from '../contexts/AuthContext';
import { resolveHome } from '../auth/resolveHome';
import '../styles/AuthPage.css';

// Three different things land here, and until 2026-08-13 all three rendered the
// same «در انتظار تأیید» card:
//   1. a real pending or rejected account          -> the account states below
//   2. the profile read failing                    -> profileError, an outage
//   3. a signed-in user with no profiles row       -> PROFILE_ROW_MISSING
// Case 2 was live in production for a night after migration 0131 revoked
// EXECUTE on private.is_admin, which the profiles SELECT policy calls. Every
// user including super_admin saw "your account is awaiting approval", which is
// a sentence about the business, not about the failure that actually happened.
// Never describe an error we did not diagnose as a state we did not observe.
export default function PendingPage() {
  const { session, profile, profileError, loading, signOut, refreshProfile } = useAuth();
  const navigate = useNavigate();
  const [retrying, setRetrying] = useState(false);

  const onLogout = async () => {
    await signOut();
    navigate('/login', { replace: true });
  };

  if (loading) return null;
  // Never trap a user who isn't actually pending/rejected here.
  if (!session || (profile && profile.status === 'approved')) {
    return <Navigate to={resolveHome(session, profile)} replace />;
  }

  const onRetry = async () => {
    setRetrying(true);
    try {
      await refreshProfile();
    } finally {
      setRetrying(false);
    }
  };

  if (!profile && profileError) {
    const missingRow = profileError === 'PROFILE_ROW_MISSING';
    return (
      <div className="auth-wrapper">
        <div className="auth-container" style={{ textAlign: 'center' }}>
          <div style={{
            width: 46, height: 46, borderRadius: '50%', margin: '0 auto 12px',
            background: '#fee2e2',
            display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 20,
          }}>⚠️</div>
          <h2 style={{ color: '#111827' }}>حساب شما خوانده نشد</h2>
          <p style={{ color: '#334155', fontSize: 13, lineHeight: 1.9 }}>
            {missingRow
              ? 'ورود شما انجام شد، اما هیچ پروفایلی برای این حساب ثبت نشده است. این یعنی حساب شما ناقص ساخته شده، نه اینکه در انتظار تأیید باشد. لطفاً به پشتیبانی اطلاع دهید.'
              : 'ورود شما انجام شد، اما خواندن اطلاعات حساب از سرور شکست خورد. این یک خطای دسترسی است و به معنای تأیید نشدن حساب شما نیست.'}
          </p>
          {!missingRow ? (
            <p style={{
              color: '#334155', fontSize: 11, lineHeight: 1.8, direction: 'ltr',
              background: '#f1f5f9', borderRadius: 6, padding: '8px 10px',
              wordBreak: 'break-word', textAlign: 'left',
            }}>{profileError}</p>
          ) : null}
          <button className="auth-button" onClick={onRetry} disabled={retrying}>
            {retrying ? 'در حال تلاش…' : 'تلاش دوباره'}
          </button>
          <button
            className="auth-button"
            style={{ background: '#475569', marginTop: 8 }}
            onClick={onLogout}
          >خروج</button>
        </div>
      </div>
    );
  }

  const rejected = profile?.status === 'rejected';

  return (
    <div className="auth-wrapper">
      <div className="auth-container" style={{ textAlign: 'center' }}>
        <div style={{
          width: 46, height: 46, borderRadius: '50%', margin: '0 auto 12px',
          background: rejected ? '#fee2e2' : '#fef3c7',
          display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 20,
        }}>{rejected ? '⛔' : '⏳'}</div>
        <h2 style={{ color: '#111827' }}>{rejected ? 'درخواست رد شد' : 'در انتظار تأیید'}</h2>
        <p style={{ color: '#334155', fontSize: 13, lineHeight: 1.9 }}>
          {rejected
            ? 'درخواست شما رد شد. برای پیگیری با ادمین تماس بگیرید.'
            : 'ثبت‌نام شما با موفقیت انجام شد. حساب شما در انتظار تأیید ادمین است. پس از تأیید می‌توانید وارد پنل شوید.'}
        </p>
        <button className="auth-button" style={{ background: '#475569' }} onClick={onLogout}>خروج</button>
      </div>
    </div>
  );
}
